/**
 * @fileoverview Reusable Zod field builders for email and mobile number inputs.
 * @description Every form schema with an email or mobile number field builds it from here rather
 * than chaining its own Zod rules, so a format change made once applies to every form. Messages
 * are translation keys (per this repo's i18n rule for validators): the mobile number message is
 * one shared key, while emailField takes each form's existing keys.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { z } from 'zod'

import { MOBILE_NUMBER_PATTERN } from './contact-fields.constants'

/**
 * @description Builds a required email field: trimmed, non-empty, and a valid email address.
 * @param {{ required: string; invalid: string }} messages - Translation keys for the empty and
 * malformed cases.
 * @returns {z.ZodString} The email field schema.
 */
export function emailField(messages: { required: string; invalid: string }) {
  return z.string().trim().min(1, messages.required).email(messages.invalid)
}

/**
 * @description Builds a required mobile number field: trimmed, and exactly 10 digits starting 6–9
 * (see MOBILE_NUMBER_PATTERN). An empty value fails the same check, so the one shared
 * `validation.mobileNumberInvalid` message covers both the missing and malformed cases.
 * @returns {z.ZodString} The mobile number field schema.
 */
export function mobileNumberField() {
  return z.string().trim().regex(MOBILE_NUMBER_PATTERN, 'validation.mobileNumberInvalid')
}
